import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, RefreshCw, Trash2, Plus } from "lucide-react";
import { adminApi, apiError } from "@/lib/api";
import { fmtMoney, Panel, PageHeader, Th, Td, Empty } from "@/components/admin/parts";

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const StateBadge = ({ state }) => {
  const map = {
    ok: { c: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", t: "OK" },
    alert70: { c: "text-amber-400 border-amber-500/40 bg-amber-500/10", t: "ALERT 70%" },
    alert90: { c: "text-orange-400 border-orange-500/40 bg-orange-500/10", t: "ALERT 90%" },
    block: { c: "text-red-400 border-red-500/40 bg-red-500/10", t: "BLOKADA 100%" },
  };
  const m = map[state] || map.ok;
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.c}`}>{m.t}</span>;
};

const ShareCell = ({ share, warn, block }) => {
  if (share == null) return <span className="text-brand-muted">— (bezpłatna)</span>;
  const color = share > block ? "text-red-400" : share > warn ? "text-amber-400" : "text-emerald-400";
  return <span className={`font-semibold ${color}`}>{share}%</span>;
};

const ProfStatus = ({ status }) => {
  const map = {
    bezpieczna: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
    "ostrzeżenie": "text-amber-400 border-amber-500/40 bg-amber-500/10",
    blokada: "text-red-400 border-red-500/40 bg-red-500/10",
  };
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${map[status] || map.bezpieczna}`}>{(status || "").toUpperCase()}</span>;
};

export default function AiCostControl() {
  const [ctrl, setCtrl] = useState(null);
  const [minRev, setMinRev] = useState(null);
  const [eco, setEco] = useState(null);
  const [budget, setBudget] = useState(null);
  const [ovHist, setOvHist] = useState([]);
  const [prof, setProf] = useState(null);
  const [active, setActive] = useState(false);
  const [form, setForm] = useState({});
  const [ovValue, setOvValue] = useState("");
  const [ovReason, setOvReason] = useState("");
  const [funcKill, setFuncKill] = useState([]);
  const [modelKill, setModelKill] = useState([]);
  const [funcBudget, setFuncBudget] = useState([]);
  const [modelBudget, setModelBudget] = useState([]);
  const [saving, setSaving] = useState(false);

  const toRows = (obj) => Object.entries(obj || {}).map(([k, v]) => ({ k, v }));

  const load = async () => {
    try {
      const c = await adminApi.aiCostControl();
      setCtrl(c.control); setMinRev(c.minCreditRevenue); setActive(!!c.controlActive);
      setForm({
        enforcement_enabled: !!c.control.enforcement_enabled,
        cost_share_warn_percent: c.control.cost_share_warn_percent,
        cost_share_block_percent: c.control.cost_share_block_percent,
        other_sale_cost_percent: c.control.other_sale_cost_percent,
        max_op_cost_pln: c.control.max_op_cost_pln ?? "",
        max_retries: c.control.max_retries,
        projection_max_output_tokens: c.control.projection_max_output_tokens,
        budget_daily_pln: c.control.budget_daily_pln ?? "",
        budget_monthly_pln: c.control.budget_monthly_pln ?? "",
        alert_thresholds: (c.control.alert_thresholds || [70, 90]).join(","),
      });
      setFuncKill(toRows(c.control.function_kill).filter((r) => r.v));
      setModelKill(toRows(c.control.model_kill).filter((r) => r.v));
      setFuncBudget(toRows(c.control.budget_by_function));
      setModelBudget(toRows(c.control.budget_by_model));
    } catch (e) { toast.error(apiError(e)); }
    adminApi.aiCostEconomics(30).then(setEco).catch(() => {});
    adminApi.aiCostBudgetStatus().then(setBudget).catch(() => {});
    adminApi.aiCostProfitability().then(setProf).catch(() => {});
    adminApi.aiCostOverrideHistory().then((d) => setOvHist(d.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        enforcement_enabled: !!form.enforcement_enabled,
        cost_share_warn_percent: Number(form.cost_share_warn_percent),
        cost_share_block_percent: Number(form.cost_share_block_percent),
        other_sale_cost_percent: Number(form.other_sale_cost_percent),
        max_op_cost_pln: numOrNull(form.max_op_cost_pln),
        max_retries: Number(form.max_retries),
        projection_max_output_tokens: Number(form.projection_max_output_tokens),
        budget_daily_pln: numOrNull(form.budget_daily_pln),
        budget_monthly_pln: numOrNull(form.budget_monthly_pln),
        alert_thresholds: String(form.alert_thresholds).split(",").map((x) => Number(x.trim())).filter((x) => x > 0),
        function_kill: Object.fromEntries(funcKill.filter((r) => r.k).map((r) => [r.k, true])),
        model_kill: Object.fromEntries(modelKill.filter((r) => r.k).map((r) => [r.k, true])),
        budget_by_function: Object.fromEntries(funcBudget.filter((r) => r.k && r.v !== "").map((r) => [r.k, Number(r.v)])),
        budget_by_model: Object.fromEntries(modelBudget.filter((r) => r.k && r.v !== "").map((r) => [r.k, Number(r.v)])),
      };
      await adminApi.aiCostControlUpdate(patch);
      toast.success("Zapisano ustawienia kontroli kosztów.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const saveOverride = async () => {
    if (!ovReason.trim() || ovReason.trim().length < 3) { toast.error("Podaj powód (min. 3 znaki)."); return; }
    try {
      await adminApi.aiCostMinOverride({ value: ovValue === "" ? null : Number(ovValue), reason: ovReason.trim() });
      toast.success(ovValue === "" ? "Przywrócono automatyczne wyliczenie." : "Zapisano ręczną wartość.");
      setOvValue(""); setOvReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!ctrl) return <div className="p-6 text-brand-sec">Ładowanie…</div>;
  const warnP = Number(form.cost_share_warn_percent) || 25;
  const blockP = Number(form.cost_share_block_percent) || 30;

  return (
    <div className="space-y-6" data-testid="admin-aicost">
      <PageHeader title="Kontrola kosztów AI" subtitle="Rentowność, budżety, limity i bezpieczniki. Koszty oznaczone jako ESTIMATED (dostawca nie zwraca realnej liczby tokenów)." />

      <div className="flex items-center gap-2 rounded-panel border border-amber-500/30 bg-amber-500/10 p-3 text-[13px] text-amber-300" data-testid="admin-aicost-estimated">
        <AlertTriangle size={16} /> Wszystkie koszty AI są szacowane (ESTIMATED). Rzeczywiste rozliczenie miesięczne w sekcji „Finanse → Rozliczenie z Emergent".
      </div>

      <Panel title="Egzekwowanie kontroli kosztów" testId="admin-aicost-enforcement">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <p className="text-brand-text">Twarde limity, blokada udziału kosztu i budżety są <b>{active ? "AKTYWNE" : "NIEAKTYWNE"}</b>.</p>
            <p className="text-[12px] text-brand-muted">Gdy wyłączone: koszty i symulacja są nadal liczone, ale nic nie jest blokowane. Płatnych funkcji Biznes nie da się włączyć bez aktywnej kontroli.</p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
            <input data-testid="admin-aicost-enforcement-toggle" type="checkbox" checked={!!form.enforcement_enabled} onChange={(e) => f("enforcement_enabled", e.target.checked)} className="h-5 w-5 accent-brand-accent" />
            <span>Egzekwuj limity</span>
          </label>
        </div>
        {!active && (
          <div className="mt-3 flex items-center gap-2 rounded-field border border-red-500/40 bg-red-500/10 p-3 text-[13px] text-red-300" data-testid="admin-aicost-inactive-banner">
            <ShieldAlert size={16} /> Nie można włączyć płatności — kontrola kosztów jest nieaktywna. Włącz egzekwowanie i zapisz, aby aktywować płatne funkcje Biznes.
          </div>
        )}
      </Panel>

      <Panel title="Symulacja rentowności planowanych cen (przed włączeniem płatności)" testId="admin-aicost-profit">
        {!prof ? <div className="text-brand-sec">Ładowanie…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr>
                <Th>Funkcja</Th><Th>Cena (kr.)</Th><Th>Przychód min. netto</Th><Th>Śr. koszt AI</Th><Th>Maks. koszt AI</Th>
                <Th>Udział (śr.)</Th><Th>Udział (maks.)</Th><Th>Marża</Th><Th>Status</Th>
              </tr></thead>
              <tbody data-testid="admin-aicost-profit-rows">
                {prof.rows.map((r) => (
                  <tr key={r.action_key} className="border-t border-brand-line">
                    <Td>{r.label} <span className="text-[11px] text-brand-muted">({r.source})</span></Td>
                    <Td>{r.credits}</Td>
                    <Td>{fmtMoney(r.min_revenue_pln)}</Td>
                    <Td>{fmtMoney(r.avg_cost_pln)}</Td>
                    <Td>{fmtMoney(r.max_cost_pln)}</Td>
                    <Td><ShareCell share={r.share_avg_percent} warn={prof.warnPercent} block={prof.blockPercent} /></Td>
                    <Td><ShareCell share={r.share_max_percent} warn={prof.warnPercent} block={prof.blockPercent} /></Td>
                    <Td>{r.margin_percent == null ? "—" : `${r.margin_percent}%`}</Td>
                    <Td><ProfStatus status={r.status} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[12px] text-brand-muted">Status wg udziału MAKS.: bezpieczna ≤ {prof.warnPercent}%, ostrzeżenie {prof.warnPercent}–{prof.blockPercent}%, blokada &gt; {prof.blockPercent}%. Koszty ESTIMATED.</p>
          </div>
        )}
      </Panel>

      <Panel title="Minimalny przychód netto z kredytu (podstawa rentowności)" testId="admin-aicost-minrev">
        {minRev && (
          <div className="space-y-2 text-sm">
            <p className="text-brand-text">
              <span className="text-2xl font-bold text-brand-accent" data-testid="admin-aicost-minrev-value">{Number(minRev.value).toFixed(6)} zł</span>
              <span className="ml-2 text-brand-sec">/ kredyt · źródło: {minRev.source === "override" ? "ręczna korekta administratora" : `pakiet „${minRev.pack_name || "—"}" (automat)`}</span>
            </p>
            {minRev.components && minRev.components.gross != null && (
              <p className="text-[12px] text-brand-muted">
                brutto {fmtMoney(minRev.components.gross)} − VAT {fmtMoney(minRev.components.vat)} − prowizja {fmtMoney(minRev.components.fee)} − inne {fmtMoney(minRev.components.other)} = netto {fmtMoney(minRev.components.net)} / {minRev.components.credits} kredytów
              </p>
            )}
          </div>
        )}
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div>
            <label className="text-xs text-brand-sec">Ręczna wartość (zł/kredyt) — puste = automat</label>
            <input data-testid="admin-aicost-ov-value" value={ovValue} onChange={(e) => setOvValue(e.target.value)} placeholder="np. 0.06" className="mt-1 w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-brand-sec">Powód korekty (wymagany, zapisywany w historii)</label>
            <div className="mt-1 flex gap-2">
              <input data-testid="admin-aicost-ov-reason" value={ovReason} onChange={(e) => setOvReason(e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm" />
              <button data-testid="admin-aicost-ov-save" onClick={saveOverride} className="shrink-0 rounded-button bg-brand-accent px-4 py-2 text-sm font-semibold text-brand-on">Zapisz</button>
            </div>
          </div>
        </div>
        {ovHist.length > 0 && (
          <div className="mt-3 text-[12px] text-brand-muted">
            <p className="mb-1 font-semibold text-brand-sec">Historia korekt:</p>
            <ul className="space-y-0.5">
              {ovHist.slice(0, 5).map((h, i) => (
                <li key={i}>{h.action === "cleared" ? "przywrócono automat" : `${h.value} zł`} · {h.reason} · {h.admin_email}</li>
              ))}
            </ul>
          </div>
        )}
      </Panel>

      <Panel title="Progi rentowności i bezpieczniki operacji" testId="admin-aicost-thresholds">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Ostrzeżenie udziału kosztu (%)" testId="admin-aicost-warn" value={form.cost_share_warn_percent} onChange={(v) => f("cost_share_warn_percent", v)} />
          <Field label="Blokada udziału kosztu (%)" testId="admin-aicost-block" value={form.cost_share_block_percent} onChange={(v) => f("cost_share_block_percent", v)} />
          <Field label="Inne koszty sprzedaży (% brutto)" value={form.other_sale_cost_percent} onChange={(v) => f("other_sale_cost_percent", v)} />
          <Field label="Maks. koszt 1 operacji (zł) — puste = brak" testId="admin-aicost-maxop" value={form.max_op_cost_pln} onChange={(v) => f("max_op_cost_pln", v)} />
          <Field label="Maks. liczba ponowień" testId="admin-aicost-retries" value={form.max_retries} onChange={(v) => f("max_retries", v)} />
          <Field label="Prognoza: maks. tokeny wyjścia" value={form.projection_max_output_tokens} onChange={(v) => f("projection_max_output_tokens", v)} />
        </div>
      </Panel>

      <Panel title="Budżety AI (PLN) i alerty" testId="admin-aicost-budgets">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Budżet dzienny platformy — puste = brak" testId="admin-aicost-daily" value={form.budget_daily_pln} onChange={(v) => f("budget_daily_pln", v)} />
          <Field label="Budżet miesięczny platformy — puste = brak" testId="admin-aicost-monthly" value={form.budget_monthly_pln} onChange={(v) => f("budget_monthly_pln", v)} />
          <Field label="Progi alertów (%, po przecinku)" value={form.alert_thresholds} onChange={(v) => f("alert_thresholds", v)} />
        </div>
        <p className="mt-2 text-[12px] text-brand-muted">Blokada 100% wstrzymuje wyłącznie kolejne operacje. Alert 90% i blokada 100% wysyłają jeden e-mail do administratora.</p>
        {budget && budget.items.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr><Th>Zakres</Th><Th>Budżet</Th><Th>Zużyto</Th><Th>%</Th><Th>Status</Th></tr></thead>
              <tbody data-testid="admin-aicost-budget-status">
                {budget.items.map((b, i) => (
                  <tr key={i} className="border-t border-brand-line">
                    <Td>{b.label || b.scope}</Td><Td>{fmtMoney(b.budgetPln)}</Td><Td>{fmtMoney(b.usedPln)}</Td>
                    <Td>{b.pct}%</Td><Td><StateBadge state={b.state} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {budget && budget.items.length === 0 && <p className="mt-3 text-[13px] text-brand-muted">Brak skonfigurowanych budżetów — kontrola budżetowa jest wyłączona.</p>}
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Budżety funkcji (PLN / miesiąc)" testId="admin-aicost-func-budget">
          <DictEditor rows={funcBudget} setRows={setFuncBudget} keyPh="np. cv_cover_letter" valPh="zł" withValue />
        </Panel>
        <Panel title="Budżety modeli (PLN / miesiąc)" testId="admin-aicost-model-budget">
          <DictEditor rows={modelBudget} setRows={setModelBudget} keyPh="np. gpt-5.4" valPh="zł" withValue />
        </Panel>
        <Panel title="Awaryjne wyłączenie funkcji (kill switch)" testId="admin-aicost-func-kill">
          <DictEditor rows={funcKill} setRows={setFuncKill} keyPh="np. business_generator" />
        </Panel>
        <Panel title="Awaryjne wyłączenie modelu (kill switch)" testId="admin-aicost-model-kill">
          <DictEditor rows={modelKill} setRows={setModelKill} keyPh="np. gpt-5.4-mini" />
        </Panel>
      </div>

      <div className="flex justify-end">
        <button data-testid="admin-aicost-save" onClick={save} disabled={saving} className="rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on disabled:opacity-60">
          {saving ? "Zapisywanie…" : "Zapisz ustawienia"}
        </button>
      </div>

      <Panel title="Ekonomia AI wg funkcji (ostatnie 30 dni)" testId="admin-aicost-eco-func" action={<button onClick={() => adminApi.aiCostEconomics(30).then(setEco)} className="inline-flex items-center gap-1 text-sm text-brand-sec hover:text-brand-text"><RefreshCw size={14} /> Odśwież</button>}>
        <EcoTable rows={eco?.byFunction} warn={warnP} block={blockP} />
      </Panel>
      <Panel title="Ekonomia AI wg modelu (ostatnie 30 dni)" testId="admin-aicost-eco-model">
        <EcoTable rows={eco?.byModel} warn={warnP} block={blockP} />
      </Panel>
    </div>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs text-brand-sec">{label}</label>
      <input data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm" />
    </div>
  );
}

function DictEditor({ rows, setRows, keyPh, valPh, withValue }) {
  const upd = (i, field, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [field]: v } : r)));
  const add = () => setRows([...rows, { k: "", v: withValue ? "" : true }]);
  const del = (i) => setRows(rows.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      {rows.length === 0 && <p className="text-[13px] text-brand-muted">Brak wpisów.</p>}
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <input value={r.k} onChange={(e) => upd(i, "k", e.target.value)} placeholder={keyPh} className="flex-1 rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm" />
          {withValue && <input value={r.v} onChange={(e) => upd(i, "v", e.target.value)} placeholder={valPh} className="w-28 rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm" />}
          <button onClick={() => del(i)} className="rounded-field border border-brand-line p-2 text-brand-muted hover:text-red-400"><Trash2 size={15} /></button>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-1 rounded-field border border-brand-line px-3 py-1.5 text-[13px] text-brand-sec hover:border-brand-accent/50"><Plus size={14} /> Dodaj</button>
    </div>
  );
}

function EcoTable({ rows, warn, block }) {
  if (!rows) return <div className="text-brand-sec">Ładowanie…</div>;
  if (rows.length === 0) return <Empty>Brak operacji w tym okresie.</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr><Th>Klucz</Th><Th>Wywołania</Th><Th>Koszt (est.)</Th><Th>Kredyty</Th><Th>Przychód min.</Th><Th>Udział</Th><Th>Marża</Th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-brand-line">
              <Td>{r.key}</Td>
              <Td>{r.calls} <span className="text-brand-muted">({r.free_calls} bezpł.)</span></Td>
              <Td>{fmtMoney(r.cost_pln)}</Td>
              <Td>{r.credits}</Td>
              <Td>{fmtMoney(r.revenue_min_pln)}</Td>
              <Td><ShareCell share={r.cost_share_percent} warn={warn} block={block} /></Td>
              <Td>{r.margin_percent == null ? "—" : `${r.margin_percent}%`}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
